<!-- src/lib/components/StudioCanvas.svelte -->
<!--
  Studio — native design surface. First real cut of the Figma
  replacement tracked in docs/superpowers/specs/2026-04-11-50-
  refinements.md items 34-40.

  Round 6 scope (deliberately narrow):
    - Infinite pan/zoom scene
    - Four tools: select, rectangle, ellipse, text
    - Scene graph persisted to /api/v1/design/nodes
    - Drag to create, drag to move, click to select, delete key
    - Click-outside to deselect
    - HiDPI canvas renderer

  Deferred to later rounds:
    - Pen tool (freeform path)
    - Image embeds (ties into brand assets)
    - Layers panel / z-order UI / bring-forward / send-back
    - Components + instances
    - Auto-layout
    - Live cursors
    - Export to PNG/SVG
    - Keyboard shortcuts for tools (currently click-to-select)
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';

  // ── Types ────────────────────────────────────────────────────────
  interface DesignNode {
    id:           string;
    workspace_id: string;
    kind:         'rect' | 'ellipse' | 'line' | 'text' | 'frame' | 'pen';
    x:            number;
    y:            number;
    width:        number;
    height:       number;
    rotation:     number;
    opacity:      number;
    fill:         string | null;
    stroke:       string | null;
    stroke_width: number;
    text_content: string | null;
    font_size:    number | null;
    z_order:      number;
    parent_id:    string | null;
    clip_content: boolean;
    created_at:   string;
    updated_at:   string;
  }

  type Tool = 'select' | 'rect' | 'ellipse' | 'text' | 'frame';

  // ── State ────────────────────────────────────────────────────────
  let canvas: HTMLCanvasElement | null = null;
  let canvasContainer: HTMLDivElement | null = null;
  let nodes      = $state<DesignNode[]>([]);
  let tool       = $state<Tool>('select');
  let selectedId = $state<string | null>(null);
  let loading    = $state(true);
  let errorMsg   = $state<string | null>(null);
  const selectedNode = $derived(
    selectedId ? nodes.find((n) => n.id === selectedId) ?? null : null
  );

  // ── Properties panel mirrors ─────────────────────────────────────
  // These keep the inputs in sync when external changes (e.g. swatch
  // clicks) update `selectedNode` after the inputs have already mounted.
  let fillMirror    = $state('');
  let strokeMirror  = $state('');
  let xMirror       = $state(0);
  let yMirror       = $state(0);
  let wMirror       = $state(0);
  let hMirror       = $state(0);
  let opacityMirror = $state(1);
  let textMirror    = $state('');
  let fontMirror    = $state(16);

  $effect(() => {
    if (!selectedNode) return;
    xMirror       = Math.round(selectedNode.x);
    yMirror       = Math.round(selectedNode.y);
    wMirror       = Math.round(selectedNode.width);
    hMirror       = Math.round(selectedNode.height);
    opacityMirror = selectedNode.opacity;
    textMirror    = selectedNode.text_content ?? '';
    fontMirror    = selectedNode.font_size ?? 16;
    fillMirror    = selectedNode.fill   ?? '';
    strokeMirror  = selectedNode.stroke ?? '';
  });

  // Viewport transform: scale = pixels per scene unit, tx/ty = world
  // origin offset in screen pixels. Identity: scale=1, tx=ty=0.
  let scale = $state(1.0);
  let tx    = $state(0);
  let ty    = $state(0);

  // Interaction state
  type Interaction =
    | { kind: 'idle' }
    | { kind: 'pan';      startX: number; startY: number; startTx: number; startTy: number }
    | { kind: 'creating'; startX: number; startY: number; endX: number; endY: number; kindTool: Tool }
    | { kind: 'moving';   nodeId: string; startWorldX: number; startWorldY: number; startNodeX: number; startNodeY: number };
  let interaction = $state<Interaction>({ kind: 'idle' });

  // Track spacebar so panning works via space+drag regardless of tool.
  let spaceHeld  = $state(false);
  let dpr        = 1;
  let rafPending = false;

  // ── Load + persist ────────────────────────────────────────────────
  const workspaceId = $derived(pmStore.activeWorkspaceId);

  async function load() {
    if (!workspaceId) { nodes = []; loading = false; return; }
    loading = true;
    errorMsg = null;
    try {
      nodes = await api<DesignNode[]>(`/api/v1/design/nodes?workspace_id=${encodeURIComponent(workspaceId)}`);
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to load scene';
      nodes = [];
    } finally {
      loading = false;
      requestRender();
    }
  }

  async function createRemote(input: Omit<DesignNode, 'id' | 'z_order' | 'created_at' | 'updated_at'>) {
    const body = {
      workspace_id: input.workspace_id,
      kind:         input.kind,
      x: input.x, y: input.y, width: input.width, height: input.height,
      rotation: input.rotation,
      opacity:  input.opacity,
      fill:          input.fill,
      stroke:        input.stroke,
      stroke_width:  input.stroke_width,
      text_content:  input.text_content,
      font_size:     input.font_size,
      parent_id:     input.parent_id,
      clip_content:  input.clip_content,
    };
    try {
      const created = await api<DesignNode>('/api/v1/design/nodes', {
        method: 'POST',
        body:   JSON.stringify(body),
      });
      nodes = [...nodes, created];
      selectedId = created.id;
      requestRender();
      return created;
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to create node';
      return null;
    }
  }

  async function patchRemote(id: string, patch: Partial<DesignNode>) {
    try {
      const updated = await api<DesignNode>(`/api/v1/design/nodes/${id}`, {
        method: 'PATCH',
        body:   JSON.stringify(patch),
      });
      nodes = nodes.map((n) => (n.id === id ? updated : n));
      requestRender();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to update node';
    }
  }

  async function deleteRemote(id: string) {
    try {
      await api(`/api/v1/design/nodes/${id}`, { method: 'DELETE' });
      nodes = nodes.filter((n) => n.id !== id);
      if (selectedId === id) selectedId = null;
      requestRender();
    } catch (e: unknown) {
      errorMsg = e instanceof Error ? e.message : 'Failed to delete node';
    }
  }

  function patchNumber(field: keyof DesignNode, raw: string) {
    if (!selectedId) return;
    const v = parseFloat(raw);
    if (!isNaN(v)) void patchRemote(selectedId, { [field]: v } as Partial<DesignNode>);
  }

  function patchString(field: keyof DesignNode, raw: string) {
    if (!selectedId) return;
    const v = raw.trim();
    if (v) void patchRemote(selectedId, { [field]: v } as Partial<DesignNode>);
  }

  // ── Coordinate helpers ───────────────────────────────────────────
  /** Screen → world. */
  function s2w(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - tx) / scale, y: (sy - ty) / scale };
  }

  /** Is the point inside the node's bbox? Scene coords. */
  function hitTest(n: DesignNode, wx: number, wy: number): boolean {
    return wx >= n.x && wx <= n.x + n.width && wy >= n.y && wy <= n.y + n.height;
  }

  /** Topmost node hit by the given scene point (iterate z desc). */
  function topHitAt(wx: number, wy: number): DesignNode | null {
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (hitTest(nodes[i], wx, wy)) return nodes[i];
    }
    return null;
  }

  // ── Render loop ──────────────────────────────────────────────────
  function requestRender() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(render);
  }

  function render() {
    rafPending = false;
    if (!canvas || !canvasContainer) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cssW = canvasContainer.clientWidth;
    const cssH = canvasContainer.clientHeight;
    if (canvas.width !== cssW * dpr || canvas.height !== cssH * dpr) {
      canvas.width  = cssW * dpr;
      canvas.height = cssH * dpr;
      canvas.style.width  = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // Background fill matching the app shell
    ctx.fillStyle = 'oklch(0.08 0.018 275)';
    ctx.fillRect(0, 0, cssW, cssH);

    // Grid dots — 40 scene-unit spacing, snapped to the viewport
    drawGrid(ctx, cssW, cssH);

    // Apply pan + zoom
    ctx.save();
    ctx.translate(tx, ty);
    ctx.scale(scale, scale);

    // Render nodes in z order
    for (const n of nodes) {
      if (!n.parent_id) renderNode(ctx, n);
    }

    // Selection outline
    if (selectedId) {
      const sel = nodes.find((n) => n.id === selectedId);
      if (sel) drawSelection(ctx, sel);
    }

    // In-progress creation preview
    if (interaction.kind === 'creating') {
      const a = s2w(interaction.startX, interaction.startY);
      const b = s2w(interaction.endX,   interaction.endY);
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const w = Math.abs(b.x - a.x);
      const h = Math.abs(b.y - a.y);
      drawPreview(ctx, interaction.kindTool, x, y, w, h);
    }

    ctx.restore();
  }

  function drawGrid(ctx: CanvasRenderingContext2D, cssW: number, cssH: number) {
    const spacing = 40 * scale;
    if (spacing < 8) return; // too dense, skip
    ctx.fillStyle = 'oklch(0.18 0.03 275 / 0.6)';
    const startX = tx % spacing;
    const startY = ty % spacing;
    for (let x = startX; x < cssW; x += spacing) {
      for (let y = startY; y < cssH; y += spacing) {
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }

  function renderNode(ctx: CanvasRenderingContext2D, n: DesignNode) {
    ctx.globalAlpha = n.opacity;

    if (n.kind === 'frame') {
      // Draw frame background and border
      if (n.fill) {
        ctx.fillStyle = n.fill;
        ctx.fillRect(n.x, n.y, n.width, n.height);
      }
      if (n.stroke) {
        ctx.strokeStyle = n.stroke;
        ctx.lineWidth = n.stroke_width / scale;
        ctx.strokeRect(n.x, n.y, n.width, n.height);
      }
      // Clip children to the frame bounds when explicitly enabled.
      ctx.save();
      if (n.clip_content) {
        ctx.beginPath();
        ctx.rect(n.x, n.y, n.width, n.height);
        ctx.clip();
      }
      const children = nodes
        .filter((child) => child.parent_id === n.id)
        .sort((a, b) => a.z_order - b.z_order);
      for (const child of children) renderNode(ctx, child);
      ctx.restore();
      ctx.globalAlpha = 1;
      return;
    }

    if (n.fill)   ctx.fillStyle   = n.fill;
    if (n.stroke) ctx.strokeStyle = n.stroke;
    ctx.lineWidth = n.stroke_width / scale;

    switch (n.kind) {
      case 'rect':
        if (n.fill)   ctx.fillRect(n.x, n.y, n.width, n.height);
        if (n.stroke) ctx.strokeRect(n.x, n.y, n.width, n.height);
        break;

      case 'ellipse': {
        ctx.beginPath();
        ctx.ellipse(
          n.x + n.width / 2,
          n.y + n.height / 2,
          Math.max(0, n.width  / 2),
          Math.max(0, n.height / 2),
          0, 0, Math.PI * 2,
        );
        if (n.fill)   ctx.fill();
        if (n.stroke) ctx.stroke();
        break;
      }

      case 'line':
        ctx.beginPath();
        ctx.moveTo(n.x, n.y);
        ctx.lineTo(n.x + n.width, n.y + n.height);
        if (n.stroke) ctx.stroke();
        break;

      case 'text': {
        const size = n.font_size ?? 16;
        ctx.font = `${size}px -apple-system, Inter, sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillStyle = n.fill ?? 'oklch(0.95 0.02 310)';
        ctx.fillText(n.text_content ?? 'Text', n.x, n.y);
        break;
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawPreview(ctx: CanvasRenderingContext2D, kind: Tool, x: number, y: number, w: number, h: number) {
    ctx.strokeStyle = 'oklch(0.80 0.26 310 / 0.85)';
    ctx.fillStyle   = 'oklch(0.72 0.28 310 / 0.15)';
    ctx.lineWidth   = 1 / scale;
    ctx.setLineDash([6 / scale, 4 / scale]);
    if (kind === 'rect' || kind === 'text' || kind === 'frame') {
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
    } else if (kind === 'ellipse') {
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, Math.max(0, w / 2), Math.max(0, h / 2), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  function drawSelection(ctx: CanvasRenderingContext2D, n: DesignNode) {
    ctx.strokeStyle = 'oklch(0.80 0.26 310)';
    ctx.lineWidth   = 2 / scale;
    ctx.setLineDash([6 / scale, 4 / scale]);
    ctx.strokeRect(n.x - 2 / scale, n.y - 2 / scale, n.width + 4 / scale, n.height + 4 / scale);
    ctx.setLineDash([]);
  }

  // ── Pointer handling ─────────────────────────────────────────────
  function screenPos(e: PointerEvent): { x: number; y: number } {
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: PointerEvent) {
    if (!canvas || !workspaceId) return;
    canvas.setPointerCapture(e.pointerId);
    const p = screenPos(e);

    // Pan: space held OR middle-mouse OR select-tool-drag-on-empty.
    if (spaceHeld || e.button === 1) {
      interaction = { kind: 'pan', startX: p.x, startY: p.y, startTx: tx, startTy: ty };
      return;
    }

    const w = s2w(p.x, p.y);

    if (tool === 'select') {
      const hit = topHitAt(w.x, w.y);
      if (hit) {
        selectedId = hit.id;
        interaction = {
          kind: 'moving',
          nodeId: hit.id,
          startWorldX: w.x,
          startWorldY: w.y,
          startNodeX: hit.x,
          startNodeY: hit.y,
        };
      } else {
        selectedId = null;
      }
      requestRender();
      return;
    }

    // Drawing tools → start a creation drag
    interaction = {
      kind: 'creating',
      startX: p.x,
      startY: p.y,
      endX: p.x,
      endY: p.y,
      kindTool: tool,
    };
    requestRender();
  }

  function onPointerMove(e: PointerEvent) {
    if (interaction.kind === 'idle') return;
    const p = screenPos(e);

    if (interaction.kind === 'pan') {
      tx = interaction.startTx + (p.x - interaction.startX);
      ty = interaction.startTy + (p.y - interaction.startY);
      requestRender();
      return;
    }

    if (interaction.kind === 'creating') {
      interaction = { ...interaction, endX: p.x, endY: p.y };
      requestRender();
      return;
    }

    if (interaction.kind === 'moving') {
      const w = s2w(p.x, p.y);
      const dx = w.x - interaction.startWorldX;
      const dy = w.y - interaction.startWorldY;
      const id = interaction.nodeId;
      nodes = nodes.map((n) =>
        n.id === id
          ? { ...n, x: interaction.kind === 'moving' ? interaction.startNodeX + dx : n.x,
                    y: interaction.kind === 'moving' ? interaction.startNodeY + dy : n.y }
          : n
      );
      requestRender();
      return;
    }
  }

  async function onPointerUp(e: PointerEvent) {
    if (!canvas) return;
    canvas.releasePointerCapture(e.pointerId);

    if (interaction.kind === 'creating' && workspaceId) {
      const a = s2w(interaction.startX, interaction.startY);
      const b = s2w(interaction.endX, interaction.endY);
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const w = Math.abs(b.x - a.x);
      const h = Math.abs(b.y - a.y);
      const kindTool = interaction.kindTool;
      // Auto-parent: if the new shape's center lands inside a frame, parent to it.
      let parentId: string | null = null;
      if (kindTool !== 'frame') {
        const cx = x + w / 2;
        const cy = y + h / 2;
        for (let i = nodes.length - 1; i >= 0; i--) {
          const candidate = nodes[i];
          if (candidate.kind === 'frame' && hitTest(candidate, cx, cy)) {
            parentId = candidate.id;
            break;
          }
        }
      }
      interaction = { kind: 'idle' };
      if (w > 4 && h > 4) {
        const base = {
          workspace_id: workspaceId,
          rotation: 0,
          opacity:  1,
          stroke_width: 1,
          text_content: null,
          font_size:    null,
          parent_id:    parentId,
          clip_content: false,
        };
        if (kindTool === 'rect') {
          await createRemote({
            ...base, kind: 'rect', x, y, width: w, height: h,
            fill: 'oklch(0.58 0.22 265)', stroke: null,
          });
        } else if (kindTool === 'frame') {
          await createRemote({
            ...base, kind: 'frame', x, y, width: w, height: h,
            fill: 'oklch(0.13 0.02 275 / 0.5)',
            stroke: 'oklch(0.72 0.28 310 / 0.6)',
          });
        } else if (kindTool === 'ellipse') {
          await createRemote({
            ...base, kind: 'ellipse', x, y, width: w, height: h,
            fill: 'oklch(0.78 0.16 85)', stroke: null,
          });
        } else if (kindTool === 'text') {
          await createRemote({
            ...base, kind: 'text', x, y, width: w, height: Math.max(h, 24),
            fill: 'oklch(0.95 0.02 310)', stroke: null,
            text_content: 'Text', font_size: Math.max(14, Math.round(h * 0.7)),
          });
        }
      }
      // Return to select after drawing
      tool = 'select';
      return;
    }

    if (interaction.kind === 'moving' && workspaceId) {
      const id = interaction.nodeId;
      interaction = { kind: 'idle' };
      const n = nodes.find((n) => n.id === id);
      if (n) {
        // Persist the final position only — no patch-per-frame spam.
        await patchRemote(id, { x: n.x, y: n.y } as Partial<DesignNode>);
      }
      return;
    }

    if (interaction.kind === 'pan') {
      interaction = { kind: 'idle' };
    }
  }

  function onWheel(e: WheelEvent) {
    if (!canvas) return;
    // Cmd/Ctrl+wheel zooms around the cursor; plain wheel pans.
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const before = s2w(px, py);
      const factor = Math.exp(-e.deltaY * 0.0015);
      scale = Math.min(8, Math.max(0.15, scale * factor));
      const after = s2w(px, py);
      tx += (after.x - before.x) * scale;
      ty += (after.y - before.y) * scale;
      requestRender();
    } else {
      tx -= e.deltaX;
      ty -= e.deltaY;
      requestRender();
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    const inInput =
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement;
    if (inInput) return;

    if (e.code === 'Space' && !spaceHeld) {
      spaceHeld = true;
      e.preventDefault();
    }
    if (selectedId && (e.key === 'Delete' || e.key === 'Backspace')) {
      e.preventDefault();
      void deleteRemote(selectedId);
    }
    // Tool shortcuts
    if (e.key === 'v' || e.key === 'V') tool = 'select';
    if (e.key === 'r' || e.key === 'R') tool = 'rect';
    if (e.key === 'f' || e.key === 'F') tool = 'frame';
    if (e.key === 'o' || e.key === 'O') tool = 'ellipse';
    if (e.key === 't' || e.key === 'T') tool = 'text';
  }
  function onKeyUp(e: KeyboardEvent) {
    if (e.code === 'Space') spaceHeld = false;
  }

  // ── Lifecycle ────────────────────────────────────────────────────
  onMount(() => {
    dpr = window.devicePixelRatio || 1;
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);
    window.addEventListener('resize',  requestRender);
    void load();
  });
  onDestroy(() => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup',   onKeyUp);
    window.removeEventListener('resize',  requestRender);
  });

  // Re-render whenever state changes
  $effect(() => {
    // Re-access to trigger dep tracking
    nodes; selectedId; tool; scale; tx; ty;
    requestRender();
  });
  // Reload when the active workspace changes
  $effect(() => { if (workspaceId) void load(); });
</script>

<div class="studio-root">
  <!-- Tool palette -->
  <aside class="tool-palette" aria-label="Studio tools">
    <button class="tool-btn" class:active={tool === 'select'} onclick={() => { tool = 'select'; }} title="Select (V)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 3l7.07 17 2.51-7.42L20.07 10 3 3z"/>
      </svg>
    </button>
    <button class="tool-btn" class:active={tool === 'rect'} disabled={!workspaceId} onclick={() => { tool = 'rect'; }} title="Rectangle (R)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="4" y="5" width="16" height="14" rx="1"/>
      </svg>
    </button>
    <button class="tool-btn" class:active={tool === 'frame'} disabled={!workspaceId} onclick={() => { tool = 'frame'; }} title="Frame (F)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="3" width="18" height="18" rx="1"/>
        <path d="M3 8h18M8 3v5M16 3v5"/>
      </svg>
    </button>
    <button class="tool-btn" class:active={tool === 'ellipse'} disabled={!workspaceId} onclick={() => { tool = 'ellipse'; }} title="Ellipse (O)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <ellipse cx="12" cy="12" rx="8" ry="6"/>
      </svg>
    </button>
    <button class="tool-btn" class:active={tool === 'text'} disabled={!workspaceId} onclick={() => { tool = 'text'; }} title="Text (T)">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 4h14M12 4v16M8 20h8"/>
      </svg>
    </button>

    <span class="palette-sep"></span>

    <div class="zoom-label" title="Zoom (⌘ + scroll to change)">
      {Math.round(scale * 100)}%
    </div>
  </aside>

  <!-- Canvas -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="canvas-container"
    bind:this={canvasContainer}
    class:panning={spaceHeld || interaction.kind === 'pan'}
    class:creating={tool !== 'select' && interaction.kind !== 'moving'}
  >
    <canvas
      bind:this={canvas}
      onpointerdown={onPointerDown}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      onwheel={onWheel}
    ></canvas>

    {#if loading}
      <div class="canvas-overlay">Loading scene…</div>
    {:else if !workspaceId}
      <div class="canvas-overlay">
        <p>Select a workspace to start designing.</p>
      </div>
    {:else if errorMsg}
      <div class="canvas-overlay error">{errorMsg}</div>
    {:else if nodes.length === 0}
      <div class="canvas-overlay hint">
        <p>Empty scene. Pick a tool and drag on the canvas.</p>
        <p class="keys">Shortcuts: <kbd>V</kbd> select · <kbd>R</kbd> rect · <kbd>O</kbd> ellipse · <kbd>T</kbd> text</p>
        <p class="keys">Hold <kbd>Space</kbd> and drag to pan · <kbd>⌘</kbd>+scroll to zoom</p>
      </div>
    {/if}
  </div>

  <!-- Round 15: Layers panel (#37) — right-side dock listing nodes
       in reverse z-order (topmost first). Click to select, bring-forward
       / send-back / delete per row. -->
  <div class="right-sidebar">
    {#if nodes.length > 0}
      <aside class="layers-panel">
        <header class="layers-header">
          <span class="layers-title">Layers</span>
          <span class="layers-count">{nodes.length}</span>
        </header>
        <div class="layers-list">
          {#each [...nodes].reverse() as node (node.id)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="layer-row"
              class:selected={selectedId === node.id}
              onclick={() => { selectedId = node.id; requestRender(); }}
            >
              <span class="layer-kind">{node.kind}</span>
              <span class="layer-label">
                {#if node.kind === 'text' && node.text_content}
                  {node.text_content.slice(0, 20)}
                {:else}
                  {node.kind} z{node.z_order}
                {/if}
              </span>
              <div class="layer-actions">
                <button
                  class="layer-btn"
                  title="Bring forward"
                  disabled={node.z_order >= Math.max(...nodes.map(n => n.z_order))}
                  onclick={(e) => { e.stopPropagation(); void patchRemote(node.id, { z_order: node.z_order + 1 }); }}
                >↑</button>
                <button
                  class="layer-btn"
                  title="Send back"
                  disabled={node.z_order <= Math.min(...nodes.map(n => n.z_order))}
                  onclick={(e) => { e.stopPropagation(); void patchRemote(node.id, { z_order: node.z_order - 1 }); }}
                >↓</button>
                <button
                  class="layer-btn danger"
                  title="Delete"
                  onclick={(e) => { e.stopPropagation(); void deleteRemote(node.id); }}
                >×</button>
              </div>
            </div>
          {/each}
        </div>
      </aside>
    {/if}

    {#if selectedNode}
      <aside class="props-panel">
        <header class="props-header">
          <span class="props-title">Properties</span>
          <span class="props-kind">{selectedNode.kind}</span>
        </header>
        <div class="props-body">
          <div class="props-row">
            <label class="props-label" for="studio-prop-x">X</label>
            <input
              id="studio-prop-x"
              class="props-input"
              type="number"
              bind:value={xMirror}
              onblur={() => patchNumber('x', String(xMirror))}
            />
            <label class="props-label" for="studio-prop-y">Y</label>
            <input
              id="studio-prop-y"
              class="props-input"
              type="number"
              bind:value={yMirror}
              onblur={() => patchNumber('y', String(yMirror))}
            />
          </div>
          <div class="props-row">
            <label class="props-label" for="studio-prop-width">W</label>
            <input
              id="studio-prop-width"
              class="props-input"
              type="number"
              bind:value={wMirror}
              onblur={() => patchNumber('width', String(wMirror))}
            />
            <label class="props-label" for="studio-prop-height">H</label>
            <input
              id="studio-prop-height"
              class="props-input"
              type="number"
              bind:value={hMirror}
              onblur={() => patchNumber('height', String(hMirror))}
            />
          </div>
          <div class="props-field">
            <label class="props-label-full" for="studio-prop-fill">Fill</label>
            <input
              id="studio-prop-fill"
              class="props-input-full"
              type="text"
              bind:value={fillMirror}
              placeholder="oklch(0.58 0.22 265)"
              onblur={() => patchString('fill', fillMirror)}
            />
          </div>
          <div class="props-field">
            <label class="props-label-full" for="studio-prop-stroke">Stroke</label>
            <input
              id="studio-prop-stroke"
              class="props-input-full"
              type="text"
              bind:value={strokeMirror}
              placeholder="none"
              onblur={() => {
                if (!selectedId) return;
                void patchRemote(selectedId, { stroke: strokeMirror.trim() || null } as Partial<DesignNode>);
              }}
            />
          </div>
          <div class="props-field">
            <label class="props-label-full" for="studio-prop-opacity">Opacity</label>
            <input
              id="studio-prop-opacity"
              class="props-input-full"
              type="number"
              min="0"
              max="1"
              step="0.05"
              bind:value={opacityMirror}
              onblur={() => patchNumber('opacity', String(opacityMirror))}
            />
          </div>
          {#if selectedNode.kind === 'text'}
            <div class="props-field">
              <label class="props-label-full" for="studio-prop-text">Text</label>
              <input
                id="studio-prop-text"
                class="props-input-full"
                type="text"
                bind:value={textMirror}
                onblur={() => patchString('text_content', textMirror)}
              />
            </div>
            <div class="props-field">
              <label class="props-label-full" for="studio-prop-font-size">Font size</label>
              <input
                id="studio-prop-font-size"
                class="props-input-full"
                type="number"
                bind:value={fontMirror}
                onblur={() => patchNumber('font_size', String(fontMirror))}
              />
            </div>
          {/if}
          {#if selectedNode.kind === 'frame'}
            <div class="props-field props-field-check">
              <input
                id="studio-prop-clip"
                type="checkbox"
                checked={selectedNode.clip_content}
                onchange={(e) => {
                  const n = selectedNode;
                  if (!selectedId || !n) return;
                  void patchRemote(selectedId, {
                    clip_content: (e.currentTarget as HTMLInputElement).checked,
                  } as Partial<DesignNode>);
                }}
              />
              <label class="props-label-full" for="studio-prop-clip">Clip content</label>
            </div>
          {/if}
          <div class="props-field">
            <div class="props-label-full">Swatches</div>
            <div class="swatches">
              {#each [
                { label: 'Purple',  fill: 'oklch(0.72 0.28 310)' },
                { label: 'Blue',    fill: 'oklch(0.58 0.22 265)' },
                { label: 'Green',   fill: 'oklch(0.72 0.20 155)' },
                { label: 'Amber',   fill: 'oklch(0.78 0.16 85)'  },
                { label: 'Red',     fill: 'oklch(0.65 0.26 25)'  },
                { label: 'Surface', fill: 'oklch(0.16 0.02 275)' },
                { label: 'None',    fill: null                   },
              ] as swatch}
                <button
                  class="swatch"
                  title={swatch.label}
                  style="background: {swatch.fill ?? 'transparent'}; {!swatch.fill ? 'border-style: dashed;' : ''}"
                  onclick={() => {
                    if (!selectedId) return;
                    void patchRemote(selectedId, { fill: swatch.fill } as Partial<DesignNode>);
                  }}
                ></button>
              {/each}
            </div>
          </div>
        </div>
      </aside>
    {/if}
  </div>
</div>

<style>
  .studio-root {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr auto; /* canvas stretches, layers panel takes its width */
    width: 100%;
    height: 100%;
    min-height: 0;
    background: oklch(0.065 0.015 275);
  }

  .right-sidebar {
    grid-row: 2;
    grid-column: 2;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* ── Tool palette ─────────────────────────────────────────────── */
  .tool-palette {
    grid-column: 1 / -1; /* span both columns */
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.55rem 0.75rem;
    background: oklch(0.095 0.018 275);
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.25);
    flex-shrink: 0;
  }
  .tool-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: 1px solid transparent;
    color: oklch(0.72 0.06 310 / 0.75);
    border-radius: 4px;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .tool-btn:hover {
    background: oklch(0.17 0.025 275);
    color: oklch(0.94 0.08 310);
  }
  .tool-btn.active {
    background: oklch(0.30 0.18 310 / 0.40);
    color: oklch(0.96 0.12 310);
    border-color: oklch(0.72 0.26 310 / 0.45);
  }
  .palette-sep {
    width: 1px;
    height: 20px;
    background: oklch(0.30 0.05 275 / 0.40);
    margin: 0 0.35rem;
  }
  .zoom-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68rem;
    color: oklch(0.70 0.06 310 / 0.75);
    min-width: 3rem;
    text-align: center;
  }

  /* ── Canvas container ─────────────────────────────────────────── */
  .canvas-container {
    flex: 1;
    min-height: 0;
    position: relative;
    overflow: hidden;
    cursor: default;
  }
  .canvas-container.creating { cursor: crosshair; }
  .canvas-container.panning  { cursor: grab; }
  .canvas-container canvas {
    display: block;
    width: 100%;
    height: 100%;
  }

  /* ── Overlays ────────────────────────────────────────────────── */
  .canvas-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: oklch(0.55 0.04 310 / 0.60);
    text-align: center;
    pointer-events: none;
    font-size: 0.8rem;
  }
  .canvas-overlay p { margin: 0 0 0.35rem 0; }
  .canvas-overlay.hint .keys {
    font-size: 0.7rem;
    color: oklch(0.55 0.04 310 / 0.45);
  }
  .canvas-overlay.hint kbd {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    padding: 0.1rem 0.32rem;
    background: oklch(0.17 0.025 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    border-radius: 3px;
    color: oklch(0.88 0.06 310);
    margin: 0 0.1rem;
  }
  .canvas-overlay.error {
    color: oklch(0.72 0.22 25 / 0.90);
    font-family: 'JetBrains Mono', monospace;
  }

  /* ── Round 15: Layers panel ─────────────────────────────────── */
  .layers-panel {
    width: 200px;
    background: oklch(0.095 0.018 275);
    border-left: 1px solid oklch(0.30 0.05 275 / 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .layers-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.20);
  }
  .layers-title {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.60 0.04 310 / 0.65);
  }
  .layers-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.58rem;
    color: oklch(0.55 0.04 310 / 0.50);
    background: oklch(0.17 0.025 275);
    padding: 0.08rem 0.3rem;
    border-radius: 8px;
  }
  .layers-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .layer-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.45rem;
    border-radius: 3px;
    cursor: pointer;
    transition: background 100ms ease;
    font-size: 0.72rem;
    color: oklch(0.78 0.04 310 / 0.75);
  }
  .layer-row:hover {
    background: oklch(0.17 0.025 275);
  }
  .layer-row.selected {
    background: oklch(0.22 0.08 310 / 0.35);
    color: oklch(0.96 0.08 310);
  }
  .layer-kind {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    color: oklch(0.60 0.05 310 / 0.55);
    text-transform: uppercase;
    flex-shrink: 0;
    width: 3rem;
  }
  .layer-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .layer-actions {
    display: flex;
    gap: 0.15rem;
    opacity: 0;
    transition: opacity 100ms ease;
  }
  .layer-row:hover .layer-actions,
  .layer-row.selected .layer-actions {
    opacity: 1;
  }
  .layer-btn {
    width: 18px;
    height: 18px;
    background: transparent;
    border: 1px solid oklch(0.30 0.05 275 / 0.35);
    color: oklch(0.72 0.05 310 / 0.70);
    border-radius: 2px;
    font-size: 0.7rem;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 100ms ease, color 100ms ease;
    padding: 0;
  }
  .layer-btn:hover:not(:disabled) {
    background: oklch(0.22 0.03 275);
    color: oklch(0.94 0.08 310);
  }
  .layer-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  .layer-btn.danger:hover:not(:disabled) {
    background: oklch(0.30 0.18 25 / 0.30);
    color: oklch(0.92 0.14 25);
    border-color: oklch(0.62 0.26 25 / 0.50);
  }

  .props-panel {
    width: 200px;
    background: oklch(0.095 0.018 275);
    border-left: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-top: 1px solid oklch(0.30 0.05 275 / 0.20);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
  }
  .props-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.55rem 0.65rem;
    border-bottom: 1px solid oklch(0.30 0.05 275 / 0.20);
  }
  .props-title {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.60 0.04 310 / 0.65);
  }
  .props-kind {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.58rem;
    color: oklch(0.55 0.04 310 / 0.50);
    background: oklch(0.17 0.025 275);
    padding: 0.08rem 0.3rem;
    border-radius: 8px;
    text-transform: uppercase;
  }
  .props-body {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0.55rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .props-row {
    display: grid;
    grid-template-columns: 1.2rem 1fr 1.2rem 1fr;
    align-items: center;
    gap: 0.25rem;
  }
  .props-field {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
  }
  .props-label {
    font-size: 0.60rem;
    font-weight: 600;
    text-transform: uppercase;
    color: oklch(0.55 0.04 310 / 0.55);
    text-align: right;
  }
  .props-label-full {
    font-size: 0.60rem;
    font-weight: 600;
    text-transform: uppercase;
    color: oklch(0.55 0.04 310 / 0.55);
  }
  .props-input {
    background: oklch(0.13 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    border-radius: 3px;
    color: oklch(0.90 0.05 310);
    font-size: 0.72rem;
    font-family: 'JetBrains Mono', monospace;
    padding: 0.22rem 0.35rem;
    width: 100%;
    transition: border-color 100ms ease;
  }
  .props-input:focus {
    outline: none;
    border-color: oklch(0.72 0.28 310 / 0.5);
  }
  .props-input-full {
    background: oklch(0.13 0.02 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.30);
    border-radius: 3px;
    color: oklch(0.90 0.05 310);
    font-size: 0.72rem;
    font-family: 'JetBrains Mono', monospace;
    padding: 0.22rem 0.35rem;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 100ms ease;
  }
  .props-input-full:focus {
    outline: none;
    border-color: oklch(0.72 0.28 310 / 0.5);
  }
  .props-field-check {
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
  }
  .tool-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  .swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .swatch {
    width: 18px;
    height: 18px;
    border-radius: 3px;
    border: 1px solid oklch(0.35 0.05 275 / 0.5);
    cursor: pointer;
    transition: transform 100ms, border-color 100ms;
    flex-shrink: 0;
  }
  .swatch:hover { transform: scale(1.15); border-color: oklch(0.72 0.28 310 / 0.6); }
</style>
